import { PATH_ROOT, PATH_USER_PROFILE } from "@/constants";
import LanguageSelect from "@/containers/user-profile/components/LanguageSelect";
import useAccount from "@/modules/account/hooks/useAccount";
import { CircleUser, LogOut, UserCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useState, useCallback, useMemo } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenuButton } from "@/components/ui/sidebar";

export default function UserDropDown() {
  const { account, onLogout, impersonateCognitoId, onStopImpersonation } =
    useAccount();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
  };

  const handleSignOut = () => {
    onLogout();
    navigate(PATH_ROOT);
  };

  const handleProfileClick = () => {
    navigate(PATH_USER_PROFILE);
  };

  const handleStopImpersonation = useCallback(async () => {
    setStoppingImpersonation(true);
    try {
      await onStopImpersonation();
    } finally {
      setStoppingImpersonation(false);
    }
  }, [onStopImpersonation]);

  const displayName = useMemo(() => {
    // For contractors we should not display their email in the dropdown
    // Yes. This is by design, don't remove this feature.
    if (account?.contractor_id) {
      return t("user.profile");
    }
    return account?.email;
  }, [account?.contractor_id, account?.email, t]);

  if (!account) return null;

  return (
    <section className="relative flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center" asChild>
          <SidebarMenuButton>
            <CircleUser strokeWidth={2} className="mr-2 w-6 h-6" />
            <span className="truncate">{displayName}</span>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="mb-3" side="right">
          <DropdownMenuItem className="px-3 py-1">
            <span className="mr-2">{t("userProfile.settings.language")}</span>
            <LanguageSelect onLanguageChange={handleLanguageChange} />
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={handleProfileClick}
            className="cursor-pointer"
          >
            <span className="p-1 flex">
              <CircleUser className="mr-2 w-4 h-4" />
              {t("user.profile")}
            </span>
          </DropdownMenuItem>
          {impersonateCognitoId && (
            <DropdownMenuItem
              onSelect={handleStopImpersonation}
              className="cursor-pointer"
              disabled={stoppingImpersonation}
            >
              <span className="p-1 flex">
                <UserCheck className="mr-2 w-4 h-4" />
                {t("user.switchToAdmin")}
              </span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={handleSignOut} className="cursor-pointer">
            <span className="p-1 flex">
              <LogOut className="mr-2 w-4 h-4" />
              {t("user.logout")}
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </section>
  );
}
